import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import query from '@/api/APIServicesV2'
import { ProductDetailsResponse } from '../interfaces/inventory'

export interface UseProductDetailsOptions
  extends Omit<
    UseQueryOptions<ProductDetailsResponse, AxiosError>,
    'queryKey' | 'queryFn'
  > {
  productId: number | null
}

// Create a unique, descriptive key for this query
const PRODUCT_DETAILS_QUERY_KEY = 'product-details'

/**
 * API function to fetch product details
 */
const fetchProductDetails = async (
  productId: number
): Promise<ProductDetailsResponse> => {
  const response = await query.get(`/containers/products/${productId}/details`)
  return response.data as ProductDetailsResponse
}

/**
 * Custom hook to fetch product details with batches and movements
 *
 * @param options Configuration options including productId and react-query options
 * @returns TanStack React Query result with product details
 */
export const useProductDetails = ({
  productId,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutes
  ...options
}: UseProductDetailsOptions) => {
  return useQuery({
    queryKey: [PRODUCT_DETAILS_QUERY_KEY, productId],
    queryFn: () => {
      if (!productId) {
        throw new Error('Product ID is required')
      }
      return fetchProductDetails(productId)
    },
    enabled: enabled && productId !== null,
    staleTime,
    ...options,
  })
}

/**
 * Helper function to get the product details query key
 * Useful for cache invalidation or prefetching
 */
export const getProductDetailsQueryKey = (productId: number) => [
  PRODUCT_DETAILS_QUERY_KEY,
  productId,
]
